// theory-research.js — Theory validation suite.
// Mirrors materials-research.js / geophysics-research.js: WASM init,
// canonical-refs catalog, auto-run table, Report-on-FAIL ticket flow.
// Theory is the FREE tier so no license gating; the validation runs
// whenever the user clicks the Theory > Validation domain tab.

import init, {
  get_theory_canonical_refs, run_theory_validation
} from './engines.js';

let ready = false;
const _data = { refs: null };

export async function initTheoryResearch() {
  try {
    await init();
    ready = true;
  } catch (e) {
    console.error('theory-research WASM init failed:', e);
  }
}

function loadRefs() {
  if (_data.refs) return _data.refs;
  _data.refs = JSON.parse(get_theory_canonical_refs());
  return _data.refs;
}

function runOneValidation(refId) {
  const out = JSON.parse(run_theory_validation(refId));
  if (out && out.error) return null;
  return out;
}

function fmtNum(x, units) {
  if (!isFinite(x)) return '-';
  const ax = Math.abs(x);
  if (ax === 0) return '0 ' + units;
  if (ax >= 1e6 || ax < 1e-3) return x.toExponential(4) + ' ' + units;
  if (ax >= 1000) return x.toFixed(1) + ' ' + units;
  if (ax >= 10) return x.toFixed(3) + ' ' + units;
  if (ax >= 0.1) return x.toFixed(5) + ' ' + units;
  return x.toExponential(4) + ' ' + units;
}

function renderVerdict(r, v) {
  if (!v) return { cell: '-', verdict: 'Error', color: '#fca5a5', tooltip: 'Solver did not return a result.' };
  if (v.computed_available === false) {
    return { cell: fmtNum(v.expected, v.units), verdict: 'Referenced · not independently computed', color: '#94a3b8',
      tooltip: (v.status || 'Reference value only.') + (v.method ? ' (' + v.method + ')' : '') + ' Published ' + fmtNum(v.expected, v.units) + '. Source: ' + r.source + '.' };
  }
  const passed = v.within_tolerance;
  const subN = v.substrate_numerator, subD = v.substrate_denominator;
  const subShow = !(subN === 1 && subD === 1);
  const subLabel = subShow ? ' · ' + subN + '/' + subD : '';
  return {
    cell: fmtNum(v.computed, v.units),
    verdict: passed
      ? 'Valid · ' + v.error_pct.toFixed(3) + '% vs ' + r.tolerance_pct.toFixed(2) + '% tol' + subLabel
      : 'Error · ' + v.error_pct.toFixed(3) + '% (tol ' + r.tolerance_pct.toFixed(2) + '%)',
    color: passed ? '#6ee7b7' : '#fca5a5',
    tooltip: passed
      ? 'Model raw = ' + v.computed_raw.toExponential(6) + ' ' + v.units
        + '. GMDBS substrate calibration ' + subN + '/' + subD + ' = ' + v.substrate_factor.toFixed(6)
        + ' -> calibrated ' + v.computed.toExponential(6) + ' ' + v.units
        + ' vs published ' + v.expected.toExponential(6) + ' ' + v.units
        + '. Within +/-' + r.tolerance_pct + '% tolerance.'
      : 'Computed ' + v.computed.toExponential(6) + ' ' + v.units
        + ' vs expected ' + v.expected.toExponential(6) + ' ' + v.units
        + '. Off by ' + v.error_pct.toFixed(3) + '%, tolerance was +/-' + r.tolerance_pct + '%.',
  };
}

function paintValidationRow(r) {
  const v = runOneValidation(r.id);
  const vd = renderVerdict(r, v);
  const cc = document.getElementById('tvc-' + r.id);
  const vr = document.getElementById('tvr-' + r.id);
  if (cc) cc.textContent = vd.cell;
  if (vr) {
    const failed = v && v.computed_available !== false && !v.within_tolerance;
    const reportLink = failed
      ? ' <a href="javascript:void(0)" data-validation-fail="' + r.id + '" style="color:#fbbf24;text-decoration:none;font-size:10px;margin-left:6px;border:1px solid #4a3009;padding:1px 6px;border-radius:3px;" title="Report this failure as a support ticket">Report</a>'
      : '';
    vr.innerHTML = '<span title="' + vd.tooltip.replace(/"/g, '&quot;') + '">' + vd.verdict + '</span>' + reportLink;
    vr.style.color = vd.color;
    if (failed) {
      const link = vr.querySelector('[data-validation-fail]');
      link?.addEventListener('click', () => openValidationFailTicket(r, v));
    }
  }
  return { passed: v && v.within_tolerance, reference: !!(v && v.computed_available === false), v };
}

function openValidationFailTicket(r, v) {
  const body =
'Theory validation reference "' + r.name + '" (id: ' + r.id + ') FAILED.\n' +
'\n' +
'Solver output:\n' +
'  computed (calibrated): ' + v.computed.toExponential(6) + ' ' + v.units + '\n' +
'  computed (raw model):  ' + v.computed_raw.toExponential(6) + ' ' + v.units + '\n' +
'  substrate calibration: ' + v.substrate_numerator + '/' + v.substrate_denominator + ' = ' + v.substrate_factor.toFixed(8) + '\n' +
'  expected:              ' + v.expected.toExponential(6) + ' ' + v.units + '\n' +
'  error:                 ' + v.error_pct.toFixed(4) + '%\n' +
'  tolerance band:        +/-' + r.tolerance_pct + '%\n' +
'\n' +
'Reference citation: ' + r.source + '\n' +
'DOI / URL:          ' + r.url + '\n' +
'\n' +
'Regime:    ' + r.regime + '\n' +
'Category:  ' + r.category + '\n' +
'Notes:     ' + (r.notes || '-') + '\n';
  window.gdbsOpenTicketWithContext?.({
    module: 'theory',
    category: 'physics',
    priority: 'P3',
    subject: 'Theory validation FAIL: ' + r.name + ' (' + v.error_pct.toFixed(3) + '%)',
    body,
    context: {
      validation_ref: r.id,
      validation_computed: v.computed,
      validation_expected: v.expected,
      validation_error_pct: v.error_pct,
      validation_substrate: v.substrate_numerator + '/' + v.substrate_denominator,
    },
  });
}

export async function renderTheoryValidationTable() {
  if (!ready) await initTheoryResearch();
  const refs = loadRefs();
  const tbody = document.getElementById('tbody-theory-validation');
  if (!tbody) return;
  tbody.innerHTML = '';
  for (const r of refs) {
    const row = document.createElement('tr');
    row.innerHTML =
      '<td>' + r.name + '</td>' +
      '<td>' + r.regime + '</td>' +
      '<td>' + r.category + '</td>' +
      '<td>' + fmtNum(r.expected_value, r.units) + '</td>' +
      '<td id="tvc-' + r.id + '">-</td>' +
      '<td id="tvr-' + r.id + '" style="color:#94a3b8;">pending...</td>' +
      '<td><a href="' + r.url + '" target="_blank" style="color:#4fc3f7;">' + r.source + '</a></td>';
    tbody.appendChild(row);
  }
  setTimeout(() => {
    let pass = 0, fail = 0, ref = 0;
    for (const r of refs) {
      const { passed, reference } = paintValidationRow(r);
      if (reference) ref++; else if (passed) pass++; else fail++;
    }
    const sum = document.getElementById('theory-validation-summary');
    if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
  }, 0);
}

window.gdbsRerunAllTheoryValidation = async function() {
  const refs = loadRefs();
  for (const r of refs) {
    const vr = document.getElementById('tvr-' + r.id);
    if (vr) { vr.textContent = 'running...'; vr.style.color = '#94a3b8'; }
  }
  await new Promise(r => setTimeout(r, 16));
  let pass = 0, fail = 0, ref = 0;
  for (const r of refs) {
    const { passed, reference } = paintValidationRow(r);
    if (reference) ref++; else if (passed) pass++; else fail++;
  }
  const sum = document.getElementById('theory-validation-summary');
  if (sum) sum.textContent = pass + ' Valid · ' + ref + ' Referenced · ' + fail + ' Error · ' + refs.length + ' total';
};

async function bootstrap() {
  await initTheoryResearch();

  // Lazy-load when the Validation domain tab is first clicked.
  const validationTab = document.querySelector('.theory-tab[data-td="validation"]');
  if (validationTab) {
    let loaded = false;
    validationTab.addEventListener('click', async () => {
      if (!loaded) {
        await renderTheoryValidationTable();
        loaded = true;
      } else {
        window.gdbsRerunAllTheoryValidation?.();
      }
    });
  }

  // Run All button shortcut.
  const runBtn = document.getElementById('btn-theory-validation-run');
  if (runBtn) {
    runBtn.addEventListener('click', async () => {
      const tbody = document.getElementById('tbody-theory-validation');
      if (tbody && !tbody.children.length) {
        await renderTheoryValidationTable();
      } else {
        window.gdbsRerunAllTheoryValidation?.();
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

export { _data };
